import dayjs from 'dayjs';

import type {
  CloseProgressStatus,
  MonthCloseActivitiesResponse,
  MonthCloseEntitiesStatusResponse,
  MonthCloseProgress,
  MonthCloseProgressStatusResponse,
} from 'pages/period-close/demo/types/monthClose';

const MONTH_FORMAT = 'YYYY-MM';
const DATE_FORMAT = 'YYYY-MM-DD';
const DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

const getMonthAnchor = (month: string) => {
  const parsed = dayjs(`${month}-01`);
  return parsed.isValid() ? parsed : dayjs().startOf('month');
};

const remapDateToMonth = (input: string, month: string): string => {
  const parsed = dayjs(input);
  if (!parsed.isValid()) return input;

  const targetMonth = getMonthAnchor(month);
  const targetDay = Math.min(parsed.date(), targetMonth.daysInMonth());

  return targetMonth.date(targetDay).format(DATE_FORMAT);
};

const remapDateTimeToMonth = (input: string, month: string): string => {
  const parsed = dayjs(input);
  if (!parsed.isValid()) return input;

  const targetMonth = getMonthAnchor(month);
  const targetDay = Math.min(parsed.date(), targetMonth.daysInMonth());

  return targetMonth
    .date(targetDay)
    .hour(parsed.hour())
    .minute(parsed.minute())
    .second(parsed.second())
    .millisecond(0)
    .format(DATE_TIME_FORMAT);
};

const getFollowingMonthDate = (month: string, dayOfMonth: number): string => {
  const followingMonth = getMonthAnchor(month).add(1, 'month');
  const safeDay = Math.min(dayOfMonth, followingMonth.daysInMonth());
  return followingMonth.date(safeDay).format(DATE_FORMAT);
};

const remapActivityIdMonthToken = (id: string, month: string): string => {
  const monthToken = getMonthAnchor(month).format('YYYY_MM');
  return id.replace(/\d{4}_\d{2}/, monthToken);
};

interface PreviousAndCurrentMonthKeys {
  previousMonth: string;
  currentMonth: string;
}

export const getPreviousAndCurrentMonthKeys = (
  referenceDate = dayjs()
): PreviousAndCurrentMonthKeys => ({
  previousMonth: referenceDate.subtract(1, 'month').format(MONTH_FORMAT),
  currentMonth: referenceDate.format(MONTH_FORMAT),
});

interface BuildCloseProgressSidebarDataOptions {
  currentMonthPercent: number;
  previousMonthPercent?: number;
}

export const buildCloseProgressSidebarData = (
  options: BuildCloseProgressSidebarDataOptions,
  referenceDate = dayjs()
): MonthCloseProgress[] => {
  const { previousMonth, currentMonth } =
    getPreviousAndCurrentMonthKeys(referenceDate);

  return [
    {
      month: previousMonth,
      close_progress_percent: options.previousMonthPercent ?? 100,
      target_date: getFollowingMonthDate(previousMonth, 7),
    },
    {
      month: currentMonth,
      close_progress_percent: options.currentMonthPercent,
      target_date: getFollowingMonthDate(currentMonth, 15),
    },
  ];
};

export const remapMonthCloseEntitiesMock = (
  response: MonthCloseEntitiesStatusResponse,
  month: string
): MonthCloseEntitiesStatusResponse => ({
  data: response.data.map((entity) => ({
    ...entity,
    due_date: remapDateToMonth(entity.due_date, month),
  })),
});

export const remapMonthCloseActivitiesMock = (
  response: MonthCloseActivitiesResponse,
  month: string
): MonthCloseActivitiesResponse => ({
  data: response.data.map((activity) => ({
    ...activity,
    id: remapActivityIdMonthToken(activity.id, month),
    date_time: remapDateTimeToMonth(activity.date_time, month),
    data: Array.isArray(activity.data)
      ? activity.data.map((entity) => ({
          ...entity,
          due_date: remapDateToMonth(entity.due_date, month),
        }))
      : activity.data,
  })),
});

export const remapMonthCloseProgressMock = (
  response: MonthCloseProgressStatusResponse,
  month: string
): MonthCloseProgressStatusResponse => ({
  data: {
    ...response.data,
    month_progress: {
      ...response.data.month_progress,
      month,
      target_date: getFollowingMonthDate(month, 15),
    },
  },
});

export const buildAllMonthsCloseProgressMock = (
  response: MonthCloseProgressStatusResponse,
  referenceDate = dayjs()
): CloseProgressStatus => ({
  data: buildCloseProgressSidebarData(
    {
      currentMonthPercent: response.data.month_progress.close_progress_percent,
      previousMonthPercent: 100,
    },
    referenceDate
  ),
});
